import { decryptData, encryptData } from './encryption';
import { abortEncryptionTransition, beginEncryptionTransition, commitEncryptionTransition } from '../firebase/encryptionTransition';

const modes = new Set(['enabled', 'disabled']);
const encrypted = (content) => typeof content === 'string' && content.startsWith('encrypted:') && content.length > 'encrypted:'.length;

export const buildEncryptionMigrationManifest = async (notes, sourceMode, targetMode, key) => {
  if (!Array.isArray(notes) || !modes.has(sourceMode) || !modes.has(targetMode) || sourceMode === targetMode || !key) throw new Error('Invalid migration preflight.');
  const ids = new Set();
  const manifest = [];
  for (const note of notes) {
    if (!note || typeof note.id !== 'string' || !note.id || typeof note.content !== 'string' || ids.has(note.id)) throw new Error('Invalid migration note.');
    ids.add(note.id);
    let plaintext = note.content;
    if (sourceMode === 'enabled') {
      if (!encrypted(note.content)) throw new Error('Invalid encrypted note.');
      plaintext = await decryptData(note.content.slice('encrypted:'.length), key);
      if (typeof plaintext !== 'string') throw new Error('Unable to decrypt note.');
    }
    manifest.push({ id: note.id, currentContent: note.content, nextContent: targetMode === 'enabled' ? `encrypted:${await encryptData(plaintext, key)}` : plaintext });
  }
  return manifest;
};

export const runEncryptionMigration = async ({ notes, key, operationId, sourceMode, targetMode, isCurrent = () => true }) => {
  if (typeof operationId !== 'string' || !operationId.trim() || !modes.has(sourceMode) || !modes.has(targetMode) || sourceMode === targetMode || !isCurrent()) throw new Error('Invalid migration request.');
  const manifest = await buildEncryptionMigrationManifest(notes, sourceMode, targetMode, key);
  if (!isCurrent()) throw new Error('Stale migration request.');
  const begin = await beginEncryptionTransition({ operationId, sourceMode, targetMode, manifest });
  if (!isCurrent()) {
    try { await abortEncryptionTransition({ operationId }); } catch (_) { /* best effort */ }
    throw new Error('Stale migration request.');
  }
  const payload = { operationId, targetMode, generation: begin.generation, manifest };
  try {
    return await commitEncryptionTransition(payload);
  } catch (firstError) {
    try {
      return await commitEncryptionTransition(payload);
    } catch (_) {
      try { await abortEncryptionTransition({ operationId }); } catch (_) { /* best effort */ }
      throw firstError;
    }
  }
};
